import React, { useEffect, useRef } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { useNavigate, useLocation } from "react-router-dom";
import { InteractionRequiredAuthError, InteractionStatus } from "@azure/msal-browser";
import Loader from "./ui/Loader";
import { loginRequest } from "../config/authConfig";
import { useUserContext } from "../context/userContext";

function AuthWrapper({ children }) {
  const { instance, accounts, inProgress } = useMsal();
  const { user } = useUserContext();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const location = useLocation();
  const loginInitiated = useRef(false);

  const LAST_ACCOUNT_KEY = "lastUsedAccount";

  useEffect(() => {
    if (location.pathname === "/manifest.json") return;

    const storedAccountId = localStorage.getItem(LAST_ACCOUNT_KEY);
    const matchingAccount = storedAccountId
      ? accounts.find(acc => acc.homeAccountId === storedAccountId)
      : null;

    const fetchToken = async (account, reason = "Initial fetch") => {
      if (!account) return;

      try {
        await instance.acquireTokenSilent({
          ...loginRequest,
          account,
        });
        localStorage.setItem(LAST_ACCOUNT_KEY, account.homeAccountId);

      } catch (error) {
        console.error(`[MSAL] ❌ Erreur token (${reason})`, error);

        if (error instanceof InteractionRequiredAuthError && !loginInitiated.current) {
          loginInitiated.current = true;
          instance.loginRedirect({
            ...loginRequest,
            account,
          });
        }
      }
    };

    const handleSilentLogin = async () => {
      if (matchingAccount) {
        await fetchToken(matchingAccount);
      } else if (accounts.length === 1) {
        await fetchToken(accounts[0]);
      } else if (accounts.length > 1 && storedAccountId) {
        const found = accounts.find(acc => acc.homeAccountId === storedAccountId);
        if (found) await fetchToken(found);
      } else if (accounts.length === 0 && !loginInitiated.current) {
        loginInitiated.current = true;
        try {
          const response = await instance.ssoSilent(loginRequest);
          localStorage.setItem(LAST_ACCOUNT_KEY, response.account.homeAccountId);
          instance.setActiveAccount(response.account);
          navigate(location.pathname);
        } catch (error) {
          if (error instanceof InteractionRequiredAuthError) {
            instance.loginRedirect(loginRequest);
          } else {
            console.error("Erreur lors du SSO Silent", error);
          }
        }
      }
    };

    if (inProgress === InteractionStatus.None) {
      handleSilentLogin();
    }
  }, [children, isAuthenticated, inProgress, instance, navigate, accounts, location]);

  if (!isAuthenticated || !user) {
    return <Loader text="Chargement des données en cours... Merci de votre patience 😊" />;
  }

  return children;
}

export default AuthWrapper;
